import { AuditChange, AuditRecord, AuditVerifyResult } from "types/questionnaire.types"

/**
 * Words for the project history (Timeline): entry titles, version numbers, the verify banner's
 * state, and how a change row names what changed. Pure, so the panel and its tests share them.
 */

// ---- entries and versions -------------------------------------------------

// Only questionnaire.saved / wave.forked are actual questionnaire *versions*; data imports, tracker
// links and deliverable exports are events in the history, not versions, so they get an event
// title, not a "Version N" that implies a questionnaire change.
export const isVersionRecord = (action: string): boolean =>
  action === "questionnaire.saved" || action === "wave.forked"

// A data import of dummy data generated for testing, not real data
export const isDummyImport = (rec: AuditRecord): boolean =>
  rec.action === "data.imported" && rec.dataSource === "dummy"

export const recordTitle = (rec: AuditRecord, versionNumber?: number): string => {
  if (versionNumber != null) return `Version ${versionNumber}`
  if (rec.action === "deliverable.exported") return "Export"
  if (rec.action === "data.imported") return isDummyImport(rec) ? "Dummy data import" : "Data import"
  if (rec.action === "data.merged") return "Data merge"
  if (rec.action === "wave.linked" || rec.action === "wave.unlinked") return "Tracker change"
  return "Event"
}

// seq -> version number. Counts only versions, oldest first, so events never get a number.
export const versionNumbers = (records: AuditRecord[]): Map<number, number> => {
  const numBySeq = new Map<number, number>()
  let v = 0
  for (const r of [...records].sort((a, b) => a.seq - b.seq)) {
    if (isVersionRecord(r.action)) {
      v += 1
      numBySeq.set(r.seq, v)
    }
  }
  return numBySeq
}

export const entryCountLabel = (n: number): string => `${n} entr${n === 1 ? "y" : "ies"}`

// Names an entry by its seq: "version 3" for a version, "data import (entry 4)" for an event.
export const seqLabel = (seq: number | null | undefined, numBySeq: Map<number, number>, records: AuditRecord[]): string => {
  if (seq == null || seq < 1) return "an unknown entry"
  const v = numBySeq.get(seq)
  if (v != null) return `version ${v}`
  const rec = records.find((r) => r.seq === seq)
  return rec ? `${recordTitle(rec).toLowerCase()} (entry ${seq})` : `entry ${seq}`
}

// ---- verify banner --------------------------------------------------------

export type VerifyState = "unavailable" | "refused" | "unreadable" | "broken" | "ok"

// "unavailable" = no verdict yet (the check couldn't run, it is asked again); "refused" = no
// verdict and none coming (the server refused the request, it is not asked again). Neither is
// ever shown as a changed history. "broken" = a record fails the check (`firstBadSeq` names it,
// `problem` says how): the history was changed outside DATA(Q).
export const verifyStateOf = (
  v: AuditVerifyResult | null | undefined,
  isError: boolean,
  refused: boolean = false
): VerifyState | null => {
  if (isError) return refused ? "refused" : "unavailable"
  if (!v) return null
  if (v.checked === false) return "unavailable"
  if (v.reason === "chain_unreadable") return "unreadable"
  if (v.valid === false) return "broken"
  if (v.valid === true) return "ok"
  // A reply with no verdict at all
  return "unavailable"
}

// ---- change rows ----------------------------------------------------------

const isVariantKind = (kind: string): boolean => kind.startsWith("variant.")

const REMAP_KINDS = new Set(["option.code.remapped", "row.code.remapped", "column.code.remapped"])

/**
 * An answer, row or column whose code changed while its label stayed. The change carries the old
 * and new codes, which are never shown: codes belong to export maps, not to the history.
 */
export const isCodeRemap = (kind: string): boolean => REMAP_KINDS.has(kind)

// The list a change is about ("option", "row", "column", ...) with its question, as one key.
const listKey = (change: AuditChange): string =>
  `${change.kind.slice(0, change.kind.indexOf("."))}\n${change.path}`

// Changes that move the answers after them, and so change their codes.
const isShift = (kind: string): boolean =>
  kind.endsWith(".added") || kind.endsWith(".removed") || kind.endsWith(".reordered")

/**
 * A change inside a questionnaire variant has the path "<variant>/<question>". Splits that prefix
 * off, so the row can name the variant and the jump can find the question. Not split:
 * variant.added / variant.removed (the path is the variant itself), and a path equal to the
 * change's label (a question-level change on a question whose own label contains "/").
 */
export const splitVariantPath = (change: AuditChange): { variant: string | null; path: string } => {
  const path = change.path ?? ""
  const slash = path.indexOf("/")
  if (slash < 1 || isVariantKind(change.kind) || path === change.label) return { variant: null, path }
  return { variant: path.slice(0, slash), path: path.slice(slash + 1) }
}

// The row's title: what changed, prefixed with its variant ("UK: Q1"). A code remap is titled by
// its question, since what it says is about the question's export codes.
export const changeTitle = (change: AuditChange): string => {
  if (isVariantKind(change.kind)) return `Variant ${change.label || change.path}`
  const { variant, path } = splitVariantPath(change)
  const name = (isCodeRemap(change.kind) ? path : "") || change.label || path
  return variant ? `${variant}: ${name}` : name
}

// The owning question, when the change is about something inside it (an answer, a row).
export const changeQuestion = (change: AuditChange): string | null => {
  if (isVariantKind(change.kind) || isCodeRemap(change.kind)) return null
  const { path } = splitVariantPath(change)
  return path && path !== change.label ? path : null
}

// The question label to jump to in the editor, or null when there is none to find.
export const changeJumpTarget = (change: AuditChange): string | null => {
  if (isVariantKind(change.kind)) return null
  const { variant, path } = splitVariantPath(change)
  if (path) return path
  return variant ? null : change.label || null
}

const LIST_REORDER_NOTES: Record<string, string> = {
  "option.reordered": "Answer order changed",
  "row.reordered": "Row order changed",
  "column.reordered": "Column order changed",
}

// Which list of a question moved (an answer / row / column reorder carries no positions).
export const reorderNote = (kind: string): string | null => LIST_REORDER_NOTES[kind] ?? null

// ---- code remaps ----------------------------------------------------------

// What a code remap left on its own means to the reader: a question's export codes changed.
export const remapNote = (change: AuditChange): string | null => {
  if (!isCodeRemap(change.kind)) return null
  const { path } = splitVariantPath(change)
  return `The export codes changed for ${path || change.label}`
}

/**
 * The changes as rows, and so what a count of them counts. Adding, removing or moving an answer
 * (row, column) changes the codes of the ones after it: those code remaps are left out when the
 * same list of the question has an added, removed or reordered row, which says what happened.
 * The remaps left (a pinned code edited, nothing moved) show as one row per list of a question,
 * in the place of the first.
 */
export const collapseRemaps = (changes: AuditChange[]): AuditChange[] => {
  const shifted = new Set(
    changes.filter((c) => !isCodeRemap(c.kind) && isShift(c.kind)).map(listKey)
  )
  const seen = new Set<string>()
  return changes.filter((c) => {
    if (!isCodeRemap(c.kind)) return true
    const key = listKey(c)
    if (shifted.has(key) || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// The change as a report or CSV may show it: a code remap without its codes.
export const withoutCodes = (change: AuditChange): AuditChange => {
  if (!isCodeRemap(change.kind)) return change
  const rest = { ...change }
  delete rest.oldValue
  delete rest.newValue
  return rest
}
